import { Role, VOTE_ANOTHER_ROUND, type GameState } from '@susdle/shared';
import type { DiscussionView, DiscussionPlayer, DiscussionResolution, SecretWordTruth } from '../discussion/types.js';
import { classifyAvatarUrl } from './stateToView.js';
import { povFromRole } from './stateToMeetingView.js';

export interface DiscussionMappingContext {
  meId: string;
}

/**
 * Maps a Discussion `GameState` to the local viewer's `DiscussionView`.
 *
 * POV stripping mirrors clue-giving: `role`/`secretWord`/`secretWordLength` are
 * SELF-tagged; `Player.clue` is PUBLIC (the '' default → `null`). `state.votes`
 * is PUBLIC — every alive client sees the full tally — and targetIds (incl. the
 * `VOTE_ANOTHER_ROUND` sentinel, which equals the client `ANOTHER_ROUND`) pass
 * through verbatim. MapSchema iteration order = vote-arrival recency; the
 * VoterCluster relies on it.
 *
 * `resolution` is `'voting'` for the live phase. Every resolved outcome rides
 * the same held-beat channel: the server sets `pendingEliminationId` while the
 * phase is still Discussion and holds before transitioning onward
 * (`commitElimination` / `commitAnotherRound` in SusdleRoom).
 *
 * - A player id → `'eliminate'`: CastGrid paints the Oxblood lock before the
 *   RoleReveal arrives.
 * - The `VOTE_ANOTHER_ROUND` sentinel → `'anotherRound' | 'split'`. The server
 *   keeps the votes intact through the beat, so the projection re-derives
 *   which outcome it is the same way the server's tally decided it: a strict
 *   majority of alive players on the sentinel is the explicit another-round
 *   call; anything short (scattered votes, ties, plurality) is the split.
 */
export function stateToDiscussionView(state: GameState, ctx: DiscussionMappingContext): DiscussionView {
  const me = state.players.get(ctx.meId);
  const role = me?.role ?? Role.Villager;
  const isGhost = role === Role.Ghost;

  const players: DiscussionPlayer[] = [];
  for (const [, player] of state.players) {
    if (!player.alive) continue; // a discussion-time player is by definition alive
    const avatar = classifyAvatarUrl(player.avatarUrl);
    const dp: DiscussionPlayer = {
      id: player.id,
      name: player.name,
      avatarUrl: player.avatarUrl,
      isDefaultAvatar: avatar.isDefaultAvatar,
      turnOrder: player.turnOrder,
      connected: player.connected,
      clue: player.clue === '' ? null : player.clue, // public; '' is the unwritten sentinel
    };
    if (avatar.defaultAvatarIndex !== undefined) dp.defaultAvatarIndex = avatar.defaultAvatarIndex;
    players.push(dp);
  }
  players.sort((a, b) => a.turnOrder - b.turnOrder);

  const votes = new Map<string, string>();
  for (const [voterId, vote] of state.votes) {
    if (vote.targetId !== '') votes.set(voterId, vote.targetId); // skip cleared/empty defensively
  }

  const secret: SecretWordTruth = isGhost
    ? { mode: 'length', value: me?.secretWordLength ?? 0 }
    : { mode: 'word', value: me?.secretWord ?? '' };

  let resolution: DiscussionResolution;
  if (state.pendingEliminationId === VOTE_ANOTHER_ROUND) {
    /* No-elimination held beat. Mirrors tally.ts's strict-majority rule over
       the alive count (`players` is exactly the alive cast). A mid-beat
       roster removal can only shrink the alive count, which never flips an
       established sentinel majority back below threshold. */
    let sentinelVotes = 0;
    for (const target of votes.values()) {
      if (target === VOTE_ANOTHER_ROUND) sentinelVotes += 1;
    }
    resolution = sentinelVotes > Math.floor(players.length / 2)
      ? { kind: 'anotherRound' }
      : { kind: 'split' };
  } else if (state.pendingEliminationId !== '') {
    resolution = { kind: 'eliminate', targetId: state.pendingEliminationId };
  } else {
    resolution = { kind: 'voting' };
  }

  return {
    meId: ctx.meId,
    /* The viewer themselves may be dead (eliminated in an earlier round,
       still watching the table vote). Defaults alive on a missing `me`,
       matching the role fallback above — the normal interactive view is
       the defensive posture. */
    meAlive: me?.alive ?? true,
    pov: povFromRole(role),
    players,
    votes,
    resolution,
    secret,
    phaseEndsAt: state.phaseEndsAt === 0 ? null : state.phaseEndsAt,
    durationMs: state.phaseDurationMs,
  };
}
